import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatYmd } from '@/shared/ptDates';
import type { NervChartPoint } from '@/shared/types/variantHistoryNerv';

// The Daily NERV Trends line (FIG-238 · UC-257): one stored ∑: NERV per PT day. A day with no
// snapshot has a null value, and `connectNulls` is off, so the line BREAKS there instead of
// joining the points on either side (REQ-053 as changed by FIG-430). A lone point between two
// gaps is drawn as a dot. What-If keeps DailyNervGraph, which still joins across gaps.

interface DailyNervSnapshotGraphProps {
  points: NervChartPoint[];
}

const money = (value: number): string =>
  `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const axisMoney = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `$${value.toFixed(0)}`;
};

const axisDate = (ymd: string): string => formatYmd(ymd).replace(/, \d{4}$/, '');

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: NervChartPoint }>;
}

const SnapshotTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg min-w-[200px]" data-testid="daily-nerv-tooltip">
      <p className="font-semibold text-popover-foreground mb-2">{formatYmd(point.date)}</p>
      {point.nerv === null ? (
        <p className="text-sm text-muted-foreground">
          {point.gap?.kind === 'not_recorded' ? 'Not recorded' : 'No snapshot'}: no value is shown for this day.
        </p>
      ) : (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">∑: NERV:</span>
            <span className="font-semibold text-popover-foreground">{money(point.nerv)}</span>
          </div>
          {point.drPercentage !== null && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">DR: SKU % that day:</span>
              <span className="text-popover-foreground">{point.drPercentage}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DailyNervSnapshotGraph: React.FC<DailyNervSnapshotGraphProps> = ({ points }) => (
  <ResponsiveContainer width="100%" height={400}>
    <LineChart data={points} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
      <XAxis dataKey="date" tickFormatter={axisDate} stroke="hsl(var(--muted-foreground))" style={{ fontSize: '12px' }} />
      <YAxis tickFormatter={axisMoney} stroke="hsl(var(--muted-foreground))" style={{ fontSize: '12px' }} />
      <Tooltip content={<SnapshotTooltip />} />
      <Line
        type="monotone"
        dataKey="nerv"
        name="∑: NERV"
        stroke="hsl(var(--chart-1))"
        strokeWidth={2}
        dot={{ r: 3 }}
        activeDot={{ r: 5 }}
        connectNulls={false}
        isAnimationActive={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

export default DailyNervSnapshotGraph;
